// The one shared geometry pipeline. Preview and every exporter consume its
// output, so what you see is exactly what you export.

import pc from 'polygon-clipping'
import type { AssetMap, DocumentModel, GenResult, HolePoly, Poly, Ring } from './types'
import { boundaryRing, distInsideBoundary, insetBoundaryRing } from './boundary'
import { makeSampler } from './fields'
import { runMode } from './modes'
import { makeRng } from './rng'
import { shapeOutline, morphOutline, morphRings, placeOutline } from './shapes'
import { compileCustomShape } from './customShape'
import { ringBBox, bboxOverlap, ensureWinding, signedArea } from './geometry'
import type { BBox } from './geometry'

export function generate(doc: DocumentModel, assets: AssetMap = {}): GenResult {
  const t0 = performance.now()
  const fields = makeSampler(doc, assets)
  const { ring: boundary, circle: boundaryCircle } = boundaryRing(doc)
  // Voronoi and flow lines clip against the margin-inset ring directly (the
  // exported panel outline stays the true boundary); placement modes apply
  // the margin in the clip stage below.
  const margin = Math.max(0, doc.clipMargin)
  const clipRing = insetBoundaryRing(doc, margin) ?? boundary
  const out = runMode(doc, fields, clipRing)

  // Seeded placement jitter, applied uniformly after every placement mode so
  // the same document always scatters identically. Voronoi emits polygons
  // and is unaffected.
  if (out.kind === 'placements' && doc.layout.jitter > 0) {
    const j = doc.layout.jitter
    const rng = makeRng(doc.layout.seed ^ 0x5f356495)
    for (const pl of out.placements) {
      pl.x += (rng() * 2 - 1) * j
      pl.y += (rng() * 2 - 1) * j
    }
  }

  let holes: HolePoly[]
  let instances: number
  if (out.kind === 'polys') {
    // Voronoi cells arrive pre-clipped and disjoint; flow-line strokes can
    // overlap each other, so the union stage runs here too (a no-op for
    // disjoint cells). Inner rings keep their CW winding.
    const raw = out.polys.map((poly) => ({
      poly: poly.map((r, i) => ensureWinding(r, i === 0)),
    }))
    instances = raw.length
    holes = unionStage(raw)
  } else {
    const placed: HolePoly[] = []
    const tol = doc.export.tolerance
    // Custom shapes compile once per document; an empty compilation falls
    // back to the circle primitive so the canvas never goes blank.
    const customPolys = doc.shape.kind === 'custom' ? compileCustomShape(doc.customShape, tol) : []
    const useCustom = doc.shape.kind === 'custom' && customPolys.length > 0
    // Circle-to-circle morphs are identity: skip morphing entirely so the
    // analytic tag survives.
    const morphable = !(doc.shape.kind === 'circle' && doc.shapeB.kind === 'circle')
    for (const pl of out.placements) {
      const size = fields.size(pl.x, pl.y)
      const angleDeg = fields.angle(pl.x, pl.y)
      const rot = pl.rot + (angleDeg * Math.PI) / 180
      const morphT = morphable ? fields.shape(pl.x, pl.y) : 0
      const morphed = morphT > 1e-6

      if (useCustom) {
        if (morphed) {
          // Morphing a custom shape uses its outer silhouette.
          const silhouette = customPolys[0][0].map(([x, y]) => [x * size, y * size] as [number, number])
          const target = shapeOutline(doc.shapeB, size, tol)
          const ring = placeOutline(morphRings(silhouette, target, Math.min(1, morphT)), pl.x, pl.y, rot)
          placed.push({ poly: [ring] })
        } else {
          for (const poly of customPolys) {
            placed.push({
              poly: poly.map((ring) =>
                placeOutline(ring.map(([x, y]) => [x * size, y * size] as [number, number]), pl.x, pl.y, rot),
              ),
            })
          }
        }
        continue
      }

      // A morphed instance is no longer analytic even when it started as a
      // circle; the STEP exporter keys exact cylinders off the tag. An empty
      // custom shape falls back to the analytic circle.
      const isCircle = (doc.shape.kind === 'circle' || doc.shape.kind === 'custom') && !morphed
      const outline = morphed
        ? morphOutline(doc.shape, doc.shapeB, size, tol, morphT)
        : shapeOutline(doc.shape, size, tol)
      const ring = placeOutline(outline, pl.x, pl.y, isCircle ? 0 : rot)
      const hole: HolePoly = { poly: [ring] }
      if (isCircle) hole.circle = { cx: pl.x, cy: pl.y, r: size / 2 }
      placed.push(hole)
    }
    instances = out.placements.length
    const clipped = clipStage(doc, placed, boundary)
    holes = unionStage(clipped)
  }

  const ms = performance.now() - t0
  return {
    boundary,
    boundaryCircle,
    holes,
    stats: { instances, holes: holes.length, ms, capped: out.capped },
  }
}

function clipStage(doc: DocumentModel, holes: HolePoly[], boundary: Ring): HolePoly[] {
  const out: HolePoly[] = []
  // The edge margin shifts every distance decision; clip mode intersects
  // against the inset ring so clipped shapes respect the margin too.
  const margin = Math.max(0, doc.clipMargin)
  const clipAgainst = insetBoundaryRing(doc, margin) ?? boundary
  if (clipAgainst.length === 0) return out // margin swallowed the boundary
  for (const hole of holes) {
    const ring = hole.poly[0]
    const cx = mean(ring, 0)
    const cy = mean(ring, 1)
    // Radius of the outline around its center, conservative.
    let rad = 0
    for (const [x, y] of ring) rad = Math.max(rad, Math.hypot(x - cx, y - cy))
    const inside =
      distInsideBoundary(doc.boundary, doc.canvas.width, doc.canvas.height, cx, cy) - margin

    if (inside >= rad) {
      out.push(hole) // fully inside, keep untouched (circle tag survives)
      continue
    }
    switch (doc.clip) {
      case 'uniform': // drop-touching against the inset band: whole holes
      case 'dropTouching':
        break // touching or outside: drop
      case 'dropOutside':
        if (inside > 0) out.push(hole) // center inside: keep whole shape
        break
      case 'clip': {
        if (inside <= -rad) break // fully outside
        const res = pc.intersection([hole.poly as [number, number][][]], [[clipAgainst as [number, number][]]])
        for (const poly of res) {
          const norm = normalizePoly(poly as Poly)
          if (norm) out.push({ poly: norm })
        }
        break
      }
    }
  }
  return out
}

function mean(ring: Ring, axis: 0 | 1): number {
  let s = 0
  for (const p of ring) s += p[axis]
  return s / ring.length
}

// Union overlapping holes so exported inner wires are guaranteed disjoint.
// Overlap candidates are found via bbox clustering (exact test for circle
// pairs); each cluster is unioned in a single call.
function unionStage(holes: HolePoly[]): HolePoly[] {
  const n = holes.length
  if (n === 0) return holes
  const boxes: BBox[] = holes.map((h) => ringBBox(h.poly[0]))
  const parent = Array.from({ length: n }, (_, i) => i)
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]]
      i = parent[i]
    }
    return i
  }
  const unite = (a: number, b: number) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[rb] = ra
  }

  // Sweep over x-sorted boxes to find candidate pairs. A runaway pair count
  // means near-total mutual overlap; abort during the sweep with guidance
  // rather than spending seconds proving the obvious.
  let pairs = 0
  const order = Array.from({ length: n }, (_, i) => i).sort((a, b) => boxes[a].minX - boxes[b].minX)
  for (let ii = 0; ii < n; ii++) {
    const i = order[ii]
    for (let jj = ii + 1; jj < n; jj++) {
      const j = order[jj]
      if (boxes[j].minX > boxes[i].maxX) break
      if (!bboxOverlap(boxes[i], boxes[j])) continue
      const ci = holes[i].circle
      const cj = holes[j].circle
      if (ci && cj) {
        const d = Math.hypot(ci.cx - cj.cx, ci.cy - cj.cy)
        if (d >= ci.r + cj.r - 1e-9) continue // bboxes touch but circles do not
      }
      if (++pairs > 250000) {
        throw new Error(
          'pattern too dense to merge overlapping holes; increase spacing or reduce hole size',
        )
      }
      unite(i, j)
    }
  }

  const clusters = new Map<number, number[]>()
  for (let i = 0; i < n; i++) {
    const r = find(i)
    const list = clusters.get(r)
    if (list) list.push(i)
    else clusters.set(r, [i])
  }

  const out: HolePoly[] = []
  for (const members of clusters.values()) {
    if (members.length === 1) {
      out.push(holes[members[0]])
      continue
    }
    // Sanity valve: a union over thousands of mutually overlapping outlines
    // is a sign of degenerate settings (spacing far below hole size across a
    // huge canvas) and can hang the worker for a long time. Fail fast with
    // guidance instead; the canvas keeps the last good result.
    if (members.length > 6000) {
      throw new Error(
        `pattern too dense to merge (${members.length} overlapping holes in one cluster); increase spacing or reduce hole size`,
      )
    }
    const polys = members.map((i) => holes[i].poly as [number, number][][])
    const merged = pc.union(polys[0], ...polys.slice(1))
    for (const poly of merged) {
      const norm = normalizePoly(poly as Poly)
      if (norm) out.push({ poly: norm })
    }
  }
  return out
}

// Outer ring CCW, inner rings CW, drop degenerate rings.
export function normalizePoly(poly: Poly): Poly | null {
  const rings: Ring[] = []
  for (let i = 0; i < poly.length; i++) {
    let ring = poly[i].slice()
    // polygon-clipping may close rings with a duplicate last vertex.
    const first = ring[0]
    const last = ring[ring.length - 1]
    if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) ring = ring.slice(0, -1)
    if (ring.length < 3) continue
    if (Math.abs(signedArea(ring)) < 1e-9) continue
    rings.push(ensureWinding(ring, i === 0))
  }
  if (rings.length === 0) return null
  return rings
}

// Panel polys cut into an aligned grid of cells (absolute coordinates kept).
// Used by STEP split and by the STL exporter's chunked triangulation.
export function splitPanelPolys(
  polys: Poly[],
  boundary: Ring,
  split: { cols: number; rows: number },
): Poly[] {
  const bb = ringBBox(boundary)
  const cols = Math.max(1, Math.round(split.cols))
  const rows = Math.max(1, Math.round(split.rows))
  const out: Poly[] = []
  const multi = polys as [number, number][][][]
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = bb.minX + ((bb.maxX - bb.minX) * c) / cols
      const x1 = bb.minX + ((bb.maxX - bb.minX) * (c + 1)) / cols
      const y0 = bb.minY + ((bb.maxY - bb.minY) * r) / rows
      const y1 = bb.minY + ((bb.maxY - bb.minY) * (r + 1)) / rows
      const cell: [number, number][] = [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
      ]
      let parts: [number, number][][][]
      try {
        parts = pc.intersection(multi, [[cell]])
      } catch {
        continue
      }
      for (const poly of parts) {
        const norm = normalizePoly(poly as Poly)
        if (norm) out.push(norm)
      }
    }
  }
  return out
}

// The interior cut coordinates a cols x rows split introduces; edges lying on
// these lines separate neighboring cells and must not grow side walls.
export function splitCutLines(
  boundary: Ring,
  split: { cols: number; rows: number },
): { xs: number[]; ys: number[] } {
  const bb = ringBBox(boundary)
  const xs: number[] = []
  const ys: number[] = []
  for (let c = 1; c < split.cols; c++) xs.push(bb.minX + ((bb.maxX - bb.minX) * c) / split.cols)
  for (let r = 1; r < split.rows; r++) ys.push(bb.minY + ((bb.maxY - bb.minY) * r) / split.rows)
  return { xs, ys }
}

// Panel region = boundary minus all holes, as a normalized MultiPolygon.
// Used by the STL and STEP exporters.
export function panelGeometry(result: GenResult): Poly[] {
  const subject: [number, number][][] = [result.boundary as [number, number][]]
  const clips = result.holes.map((h) => h.poly as [number, number][][])
  const diff = clips.length > 0 ? pc.difference([subject], ...clips) : [[result.boundary]]
  const polys: Poly[] = []
  for (const poly of diff) {
    const norm = normalizePoly(poly as Poly)
    if (norm) polys.push(norm)
  }
  return polys
}
